import React from "react";
import {
  Typography,
  Link,
  Stack,
  Divider,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

function formatUsPhone(raw) {
  const d = String(raw || "").replace(/\D/g, "");
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return raw || "";
}

/**
 * Specialty rows worth showing (excludes empty / “Unknown program” placeholders).
 */
function getDisplayableSpecialties(meta) {
  const raw = Array.isArray(meta.specialties) ? meta.specialties : [];
  return raw.filter((s) => {
    const n = String(s?.name || s?.code || "").trim();
    if (!n) return false;
    if (/^unknown( program)?$/i.test(n)) return false;
    return true;
  });
}

/**
 * ER: use explicit CMS-linked flag when present on the marker (`hasEmergencyDepartment` or `hasEr`).
 * Otherwise "Unknown" until the layer pipeline adds facility-level ER attribution.
 */
function getEmergencyRoomDisplay(meta) {
  const explicit = meta.hasEmergencyDepartment ?? meta.hasEr ?? meta.emergencyDepartmentPresent;
  if (explicit === true) {
    return { value: "Yes", note: null };
  }
  if (explicit === false) {
    return { value: "No", note: null };
  }
  return {
    value: "Unknown",
    note: "Emergency department (ER) Yes/No from CMS facility data is not on this layer yet. It can be wired when sourced.",
  };
}

function InfoRow({ label, children }) {
  return (
    <Typography variant="body2" component="div" sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
      <Box component="span" fontWeight={600} color="text.secondary" sx={{ minWidth: "7rem" }}>
        {label}
      </Box>
      <Box component="span" flex={1}>
        {children}
      </Box>
    </Typography>
  );
}

/**
 * Teaching Hospitals (All GME) — CMS HCRIS-derived markers.
 * Name lives in the dialog title; body is address, phone, ER, optional specialties accordion.
 */
const GmeTeachingHospitalDetailContent = ({ marker, layerMeta }) => {
  const meta = marker.meta && typeof marker.meta === "object" ? marker.meta : {};

  const phoneRaw = marker.phone || "";
  const phoneDisplay = formatUsPhone(phoneRaw);
  const telDigits = phoneRaw.replace(/\D/g, "");
  const telHref =
    telDigits.length >= 10 ? `tel:+1${telDigits.slice(-10)}` : "";

  const cityState = [marker.city, marker.state].filter(Boolean).join(", ");
  const street = (marker.address || "").trim();

  const er = getEmergencyRoomDisplay(meta);
  const specialtyList = getDisplayableSpecialties(meta);
  const showAccordion = specialtyList.length > 0;

  const sourceUrl = layerMeta?.sourceUrl || "";
  const dataYear = layerMeta?.dataYear || "";

  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        {street ? (
          <Typography variant="body2" color="text.primary">
            {street}
          </Typography>
        ) : null}
        {cityState ? (
          <Typography variant="body2" color="text.primary">
            {cityState}
          </Typography>
        ) : null}

        <InfoRow label="Phone">
          {telHref ? (
            <Link href={telHref} variant="body2">
              {phoneDisplay || phoneRaw}
            </Link>
          ) : phoneDisplay || phoneRaw ? (
            <Typography variant="body2" component="span">
              {phoneDisplay || phoneRaw}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" component="span">
              Not listed
            </Typography>
          )}
        </InfoRow>

        <InfoRow label="ER">
          <Box>
            <Typography variant="body2" component="span">
              {er.value}
            </Typography>
            {er.note ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {er.note}
              </Typography>
            ) : null}
          </Box>
        </InfoRow>
      </Stack>

      {showAccordion ? (
        <Accordion
          defaultExpanded={false}
          disableGutters
          elevation={0}
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="gme-additional-content">
            <Typography variant="subtitle2">Additional Features</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              GME specialties reported on the hospital cost report (when line-level detail exists).
            </Typography>
            <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2.5 }}>
              {specialtyList.map((s, i) => {
                const name = String(s?.name || s?.code || "").trim();
                const fte = s?.fte != null ? Number(s.fte).toFixed(2) : null;
                return (
                  <Typography key={i} component="li" variant="body2">
                    {name}
                    {fte != null ? ` · ${fte} FTE` : ""}
                  </Typography>
                );
              })}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ) : null}

      {sourceUrl ? (
        <>
          <Divider />
          <Link href={sourceUrl} target="_blank" rel="noopener noreferrer" variant="body2">
            CMS Hospital Cost Reports (Form 2552-10)
          </Link>
          {(dataYear || layerMeta?.lastUpdated) && (
            <Typography variant="caption" color="text.secondary" display="block">
              {dataYear ? `Data year: ${dataYear}` : null}
              {dataYear && layerMeta?.lastUpdated ? " · " : null}
              {layerMeta?.lastUpdated
                ? `Layer updated ${new Date(layerMeta.lastUpdated).toLocaleDateString()}`
                : null}
            </Typography>
          )}
        </>
      ) : null}
    </Stack>
  );
};

export default GmeTeachingHospitalDetailContent;
